// useFileAttachments.js — hook reutilizável pra drag-drop + file picker.
//
// Sprint v8.3 — wire em qualquer composer (SessionComposer, CodeComposer,
// ChatHomeComposer). State + handlers + helper de serializar attachments pra prompt
// textual (chat.js GUARDED — não enviamos blob real).

import { useEffect, useRef, useState } from 'react';
import { loadAttachmentDraft, saveAttachmentDraft } from '../lib/composer/attachmentDrafts.js';

export const MAX_FILE_SIZE = 25 * 1024 * 1024;
export const MAX_FILES = 10;
// Tamanho máximo de TEXTO que entra inline no prompt — evita estourar
// context window com .md gigantes. Acima disso, só metadata.
export const MAX_TEXT_CONTENT_CHARS = 30000;

// Onda 1 (anti-travamento): teto de BYTES lido de um arquivo de texto. Antes
// readFileAsText carregava o arquivo INTEIRO na RAM (um log/.csv de centenas de MB
// solto na aba Contexto ficava residente por completo) e só truncava a 30k chars no
// serialize. Como o modelo nunca vê além de MAX_TEXT_CONTENT_CHARS, ler mais que
// isto é puro desperdício. 512KB cobre 30k chars com folga (até UTF-8 multibyte) e
// ainda deixa o marcador "[…truncado]" disparar. Só uma FATIA do File é lida.
// Local (não exportado): só o ingestFiles deste módulo usa.
const MAX_TEXT_READ_BYTES = 512 * 1024;

// Extensões/mimetypes que tratamos como texto e injetamos conteúdo no prompt.
// Tudo mais (imagem, PDF, binário) entra só como metadata.
const TEXT_EXTENSIONS = [
  '.md', '.markdown', '.mdx',
  '.txt', '.text', '.log',
  '.json', '.jsonl', '.ndjson',
  '.csv', '.tsv',
  '.yaml', '.yml',
  '.xml', '.html', '.htm',
  '.js', '.jsx', '.ts', '.tsx', '.mjs', '.cjs',
  '.py', '.rb', '.go', '.rs', '.swift', '.kt', '.java',
  '.c', '.cpp', '.h', '.hpp', '.cs',
  '.sh', '.bash', '.zsh',
  '.sql',
  '.toml', '.ini', '.env', '.conf', '.cfg',
  '.vue', '.svelte', '.astro',
  '.css', '.scss', '.sass', '.less',
];

const TEXT_MIMETYPES = [
  'text/', 'application/json', 'application/xml', 'application/javascript',
  'application/x-yaml', 'application/yaml', 'application/x-sh',
];

function isTextFile(file) {
  const name = (file.name || '').toLowerCase();
  for (const ext of TEXT_EXTENSIONS) if (name.endsWith(ext)) return true;
  const type = (file.type || '').toLowerCase();
  for (const mime of TEXT_MIMETYPES) if (type.startsWith(mime)) return true;
  return false;
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

/**
 * Serialização LEGADA (só metadata, sem conteúdo). Mantida pra compat com
 * CodeComposer + outros chamadores antigos. Pra incluir o conteúdo dos
 * arquivos texto no prompt, use `serializeAttachmentsWithContent`.
 */
export function serializeAttachmentsToPrompt(value, attachments) {
  if (!attachments || attachments.length === 0) return value;
  const lines = attachments.map(
    (a) =>
      `- ${a.name} (${(a.size / 1024).toFixed(1)} KB${a.type ? `, ${a.type}` : ''})`,
  );
  return `${value}\n\nAnexos (${attachments.length}):\n${lines.join('\n')}`;
}

/**
 * Serialização RICA: inclui o conteúdo de arquivos texto inline no prompt
 * via blocos `<file>` (XML-ish, fácil pro LLM parsear). Arquivos não-texto
 * ou que excedem MAX_TEXT_CONTENT_CHARS entram só como metadata.
 *
 * Usado pelo CouncilView e demais composers que querem que o LLM realmente
 * "leia" o anexo, não só saber que existe.
 */
export function serializeAttachmentsWithContent(value, attachments) {
  if (!attachments || attachments.length === 0) return value;
  const blocks = [];
  for (const a of attachments) {
    const sizeKb = (a.size / 1024).toFixed(1);
    if (a.textContent != null) {
      const truncated = a.textContent.length > MAX_TEXT_CONTENT_CHARS;
      const body = truncated
        ? a.textContent.slice(0, MAX_TEXT_CONTENT_CHARS) + '\n\n[…truncado]'
        : a.textContent;
      blocks.push(
        `<file name="${a.name}" size="${sizeKb}KB"${a.type ? ` type="${a.type}"` : ''}>\n${body}\n</file>`,
      );
    } else {
      // Sem conteúdo (binário, imagem, etc.) — só metadata.
      blocks.push(
        `<file name="${a.name}" size="${sizeKb}KB"${a.type ? ` type="${a.type}"` : ''} content="binary, não inlinable" />`,
      );
    }
  }
  return `${value}\n\n${blocks.join('\n\n')}`;
}

// F13-E7-T01 — anexo de TEXTO entra com CONTEÚDO em TODO turno do CHAT (antes só
// o 1º, que chega pré-serializado do ChatWorkspace — o marcador `<file name=`
// evita o double-wrap). Turno de Code segue o fluxo atual (imagens via
// buildUserContent; texto não inlinado). Pure.
export function chatTurnTextWithAttachments(isChat, text, attachments) {
  const t = String(text ?? '');
  if (!isChat || !attachments || attachments.length === 0 || t.includes('<file name=')) return text;
  return serializeAttachmentsWithContent(t, attachments);
}

// `options` lets a caller widen the limits without touching the defaults that
// every existing composer relies on (byte-identical when called with no args).
// The Context tab passes a bigger maxFileSize/maxFiles so it can accept large
// media (vídeos) — those are kept as File refs (object-URL preview), never read
// into memory as base64, so a high cap is safe.
export function useFileAttachments(options = {}) {
  const draftKey = options.draftKey;
  const [draftReady, setDraftReady] = useState(!draftKey);
  const maxFileSize = options.maxFileSize ?? MAX_FILE_SIZE;
  const maxFiles = options.maxFiles ?? MAX_FILES;
  const [attachments, setAttachments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!draftKey) return;
    let cancelled = false;
    setDraftReady(false);
    loadAttachmentDraft(draftKey).then((saved) => {
      if (!cancelled) { setAttachments(saved); setDraftReady(true); }
    }).catch(() => {
      if (!cancelled) setUploadError('Não foi possível recuperar anexos salvos.');
    });
    return () => { cancelled = true; };
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey || !draftReady) return;
    saveAttachmentDraft(draftKey, attachments).catch(() => setUploadError('Não foi possível salvar os anexos para recuperação.'));
  }, [draftKey, draftReady, attachments]);

  async function ingestFiles(fileList) {
    if (!fileList || fileList.length === 0) return;
    setUploadError(null);
    const incoming = Array.from(fileList);
    if (attachments.length + incoming.length > maxFiles) {
      setUploadError(`Máximo ${maxFiles} anexos`);
      return;
    }
    const accepted = [];
    for (const f of incoming) {
      if (f.size > maxFileSize) {
        setUploadError(`${f.name}: excede ${Math.round(maxFileSize / (1024 * 1024))} MB`);
        continue;
      }
      const id = `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const isImage = (f.type || '').startsWith('image/');
      const isText = isTextFile(f);
      let preview = null;
      let textContent = null;
      if (isImage) {
        try {
          preview = await readFileAsDataURL(f);
        } catch {
          preview = null;
        }
      } else if (isText) {
        try {
          // Lê só uma fatia limitada (não o arquivo inteiro na RAM). `File.slice`
          // devolve um Blob; readFileAsText aceita Blob. Ver MAX_TEXT_READ_BYTES.
          const slice = typeof f.slice === 'function' ? f.slice(0, MAX_TEXT_READ_BYTES) : f;
          textContent = await readFileAsText(slice);
        } catch {
          textContent = null;
        }
      }
      accepted.push({
        id,
        name: f.name,
        size: f.size,
        type: f.type || '',
        preview,
        textContent,
        kind: isImage ? 'image' : isText ? 'text' : 'binary',
        file: f,
      });
    }
    if (accepted.length > 0) {
      setAttachments((prev) => [...prev, ...accepted]);
    }
  }

  function removeAttachment(id) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  function clear() {
    setAttachments([]);
    setUploadError(null);
  }

  function onDropZoneDragOver(e) {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  }

  function onDropZoneDragLeave(e) {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  }

  function onDropZoneDrop(e) {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    ingestFiles(e.dataTransfer.files);
  }

  function onFileInputChange(e) {
    ingestFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  return {
    // state
    draftReady,
    attachments,
    dragOver,
    uploadError,
    fileInputRef,
    // ops
    removeAttachment,
    restoreAttachments: setAttachments,
    clear,
    setUploadError,
    openFilePicker,
    // Mesmo caminho de ingestão usado pelo picker/drop. Exposto pra que o
    // smart-paste (Cmd+V de uma imagem) alimente File objects pela MESMA via,
    // sem inventar um store paralelo.
    addFiles: ingestFiles,
    // dom handlers (spread em form/wrapper)
    dropZoneHandlers: {
      onDragOver: onDropZoneDragOver,
      onDragLeave: onDropZoneDragLeave,
      onDrop: onDropZoneDrop,
    },
    fileInputHandlers: {
      onChange: onFileInputChange,
    },
  };
}
